import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { MetricCard } from "../../../components/PromotionIQ/MetricCard";
import { geoFencingMetrics, formatCurrency, formatPercent, formatNumber } from "../../../data/PromotionIQ/PromotionData";
import { MapPin, Eye, Store, ShoppingCart, Target, DollarSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import React from "react";

const ImpactPanel = () => {
  const metrics = geoFencingMetrics.geo_fencing;

  const funnelData = [
    { stage: "Impressions", value: metrics.impressions, icon: Eye },
    { stage: "Store Visits", value: metrics.store_visits, icon: Store },
    { stage: "Purchases", value: metrics.purchases, icon: ShoppingCart },
    { stage: "Incremental Units", value: metrics.incremental_units, icon: Target },
  ];

  const comparisonData = [
    { tool: "Geo-Fencing", cost_per_unit: metrics.cost_per_incremental_unit, lift: 24 },
    { tool: "Display", cost_per_unit: 3.45, lift: 18 },
    { tool: "Feature", cost_per_unit: 2.95, lift: 22 },
    { tool: "Price-Only", cost_per_unit: 4.12, lift: 12 },
  ];

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Impact Panel: Geo-Fencing"
        question="Is digital proximity actually paying back?"
      />

      {/* Geo-Fencing Funnel */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-primary" />
          <h3 className="section-header">Geo-Fencing Performance Funnel</h3>
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          {funnelData.map((item, index) => (
            <div key={item.stage} className="relative">
              <div className="text-center p-6 border border-border bg-gradient-to-b from-primary/10 to-transparent">
                <item.icon className="h-6 w-6 text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold">{formatNumber(item.value)}</p>
                <p className="text-sm text-muted-foreground">{item.stage}</p>
              </div>
              {index < funnelData.length - 1 && (
                <div className="absolute top-1/2 -right-2 transform -translate-y-1/2 text-muted-foreground">
                  →
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Conversion Rates */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="text-center p-4 bg-muted/30">
            <p className="text-3xl font-bold text-primary">{formatPercent(metrics.store_visit_conversion / 100)}</p>
            <p className="text-sm text-muted-foreground">Impression → Store Visit</p>
          </div>
          <div className="text-center p-4 bg-muted/30">
            <p className="text-3xl font-bold text-success">{formatPercent(metrics.purchase_conversion / 100)}</p>
            <p className="text-sm text-muted-foreground">Store Visit → Purchase</p>
          </div>
          <div className="text-center p-4 bg-muted/30">
            <p className="text-3xl font-bold text-foreground">
              {((metrics.incremental_units / metrics.impressions) * 100).toFixed(2)}%
            </p>
            <p className="text-sm text-muted-foreground">Full Funnel Conversion</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <MetricCard
          label="Geo-Fenced Impressions"
          value={formatNumber(metrics.impressions)}
          subValue="Total reach"
          variant="default"
        />
        <MetricCard
          label="Incremental Units"
          value={formatNumber(metrics.incremental_units)}
          subValue="Attributed to geo-fencing"
          variant="success"
        />
        <MetricCard
          label="Cost per Incr. Unit"
          value={`$${metrics.cost_per_incremental_unit.toFixed(2)}`}
          subValue="Efficiency metric"
          variant="highlight"
        />
        <MetricCard
          label="Store Visit Conv."
          value={formatPercent(metrics.store_visit_conversion / 100)}
          subValue="From impression"
          variant="default"
        />
      </div>

      {/* Comparison Chart */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Tool Comparison: Cost per Incremental Unit</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={comparisonData} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
              <XAxis type="number" stroke="hsl(220, 10%, 55%)" fontSize={12} tickFormatter={(v) => `$${v}`} />
              <YAxis type="category" dataKey="tool" stroke="hsl(220, 10%, 55%)" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: "hsl(220, 18%, 13%)", border: "1px solid hsl(220, 15%, 25%)", borderRadius: "0" }}
                formatter={(value: number) => [`$${value.toFixed(2)}`, "Cost/Unit"]}
              />
              <Bar 
                dataKey="cost_per_unit" 
                fill="hsl(32, 95%, 55%)"
                label={{ position: 'right', fill: 'hsl(220, 10%, 55%)', fontSize: 12, formatter: (v: number) => `$${v.toFixed(2)}` }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Lift Comparison */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Incrementality Lift Comparison</h3>
        <div className="grid grid-cols-4 gap-4">
          {comparisonData.map((item) => (
            <div 
              key={item.tool} 
              className={`p-4 text-center ${item.tool === "Geo-Fencing" ? "border-2 border-primary bg-primary/10" : "border border-border"}`}
            >
              <p className="text-sm text-muted-foreground">{item.tool}</p>
              <p className="text-3xl font-bold text-primary">+{item.lift}%</p>
              <p className="text-xs text-muted-foreground">Incr. Lift</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This brings modern retailer tools like geo-fencing into the <span className="text-primary font-bold">same economic lens</span> as traditional tactics. 
          Compare digital proximity ROI directly against display, feature, and price-only approaches.
        </p>
      </div>
    </div>
  );
};

export default ImpactPanel;
